// Read snapshots only: writes and permission decisions never use this cache.
// Author: Akarsh Singh | 2026-09-26 | Added a read() helper and widened the mutation
// matcher to cover marketplace order, return and dispatch writes, so the supplier order
// and return lists can share this store and still refresh the moment the supplier acts on
// a row. Widening only ever causes extra invalidation, never a stale read.
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';

// How long a snapshot may be served before the store goes back to the API.
const DEFAULT_TTL_MS = 30_000;

interface Entry {
  subject: BehaviorSubject<any>;
  updatedAt: number;
  inFlight?: Promise<any>;
}

@Injectable({ providedIn: 'root' })
export class SupplierScreenSWRService {
  private cache = new Map<string, Entry>();
  private epoch = 0;
  constructor(private cookies: CookieService) {}

  keyOf(screen: string, ...query: unknown[]): string {
    return JSON.stringify([
      ...['userId', 'userRole', 'roleId', 'companyId', 'clientId', 'tokenId', 'tokenHash'].map(k => this.cookies.get(k)),
      screen, ...query,
    ]);
  }

  getCached(key: string, ttlMs: number = DEFAULT_TTL_MS): any {
    const entry = this.cache.get(key);
    return entry && Date.now() - entry.updatedAt < ttlMs ? this.clone(entry.subject.value) : null;
  }

  /**
   * Cache-first read. Serves a snapshot that is still inside its window, otherwise fetches.
   * refresh() rejects with 'Snapshot superseded' when a write invalidates the store while a
   * read is in flight; that is correct for a cache but a caller asking to read must not be
   * handed an error it would show as a failure, so the read is retried once against the new
   * generation. One retry only: a second invalidation means a genuinely concurrent write,
   * and the caller's own reload covers it.
   * Author: Akarsh Singh | 2026-09-26
   */
  async read(key: string, fetcher: () => Promise<any>, ttlMs: number = DEFAULT_TTL_MS): Promise<any> {
    const cached = this.getCached(key, ttlMs);
    if (cached !== null) return cached;
    try {
      return await this.refresh(key, fetcher);
    } catch (error: any) {
      if (!SupplierScreenSWRService.isSupersededError(error)) throw error;
    }
    try {
      return await this.refresh(key, fetcher);
    } catch (error: any) {
      if (!SupplierScreenSWRService.isSupersededError(error)) throw error;
      // Two writes landed while this read was running. Rather than report a load failure
      // the caller would show as an error, fetch directly and skip caching this one.
      return fetcher();
    }
  }

  /** True when refresh() bailed out because a write invalidated the store mid-read. */
  static isSupersededError(error: any): boolean {
    return /Snapshot superseded/.test(String(error?.message ?? error));
  }

  refresh(key: string, fetcher: () => Promise<any>): Promise<any> {
    let entry = this.cache.get(key);
    if (!entry) {
      if (this.cache.size >= 40) this.cache.delete(this.cache.keys().next().value);
      entry = { subject: new BehaviorSubject(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    if (entry.inFlight) return entry.inFlight.then(value => this.clone(value));
    const target = entry;
    const epoch = this.epoch;
    const task = Promise.resolve().then(fetcher).then(value => {
      if (epoch !== this.epoch || this.cache.get(key) !== target) throw new Error('Snapshot superseded');
      target.updatedAt = Date.now();
      target.subject.next(this.clone(value));
      return value;
    }).finally(() => { if (target.inFlight === task) target.inFlight = undefined; });
    entry.inFlight = task;
    return task.then(value => this.clone(value));
  }

  invalidateAll(): void { this.epoch++; this.cache.clear(); }

  isMutation(url: string, method: string): boolean {
    if (/^(GET|HEAD|OPTIONS)$/i.test(method)) return false;
    const path = url.split('?')[0];
    // Author: Akarsh Singh | 2026-09-28 | 'chat' added for the inbox. The inbox row set is
    // built from several chat sources written by more than one module, so the match is on any
    // path segment containing chat rather than one module's prefix.
    if (!/\/(quote|rfq|supplier|buyer|purchaseOrder|users|services|marketplace|order|return|dispatch|\w*chat\w*)\//i.test(path)) return false;
    return !/^(get|fetch|search|filter|check|verify|generate|export)/i.test(path.split('/').pop() || '');
  }

  private clone(value: any): any { return value == null ? value : JSON.parse(JSON.stringify(value)); }
}
